import React, { useState } from 'react';
import Accordion from 'ui/modules/Accordion';
import { asUserAuthor } from 'domain/shared/author';
import { CommunityCompanyNoteDTO } from 'types/company/communityNotes';
import { TinyCommunity } from 'types/company/community';
import NoteComment from './NoteComment';
import CommentForm from './CommentForm';
import { useCreateComment, useUpdateComment } from './hooks';
import useNotify from 'hooks/useNotify';
import useCommunityRole from 'hooks/useCommunityRole';
import Timestamp from 'ui/elements/Timestamp';
import UserProfileDialog from 'domain/users/UserProfile/UserProfileDialog';

interface Props {
  note: CommunityCompanyNoteDTO;
  community: TinyCommunity;
  companyId: number;
  isExpanded?: boolean;
  index: number;
}

export default function NoteItem({ note, community, companyId, isExpanded, index }: Props) {
  const [userDialogId, setUserDialogId] = useState('');
  const notify = useNotify();
  const access = useCommunityRole(community.slug);
  const canWrite = access.hasScope('Write', 'Community.CompanyNotes');

  const [createComment] = useCreateComment(community.id, companyId, note.id, {
    onSuccess: () => notify('success', 'Comment added'),
    onFailure: () => notify('error', 'Failed to add comment'),
  });

  const [updateComment] = useUpdateComment(community.id, companyId, note.id, {
    onSuccess: () => notify('success', 'Comment updated'),
    onFailure: () => notify('error', 'Failed to update comment'),
  });

  const handleCreateComment = async (text: string) => {
    await createComment({ text });
  };

  const handleUpdateComment = async (commentId: string, text: string) => {
    await updateComment({ commentId, data: { text } });
  };

  const author = asUserAuthor({
    cwUserId: note.author.cwUserId,
    name: note.author.name || undefined,
    imageUrl: note.author.imageUrl || undefined,
  });

  const onAuthorClick = note.author.cwUserId ? () => setUserDialogId(note.author.cwUserId) : undefined;

  return (
    <>
      <Accordion
        title={<div className="u-content-padding-y">{note.title}</div>}
        paddingHeader="none"
        paddingContent="none"
        border={index === 0 ? 'none' : 'top'}
        isExpanded={isExpanded}
      >
        <div className="u-content-spacing-bottom">
          <p>{note.description}</p>
          <div
            className="text-tiny text-grey u-half-quarter-top u-flex--gap-quarter u-flex-align-center"
            style={{ lineHeight: 1 }}
          >
            <button onClick={onAuthorClick}>{author.name}</button>{' '}
            <Timestamp timestamp={note.createdAt} tooltipPlacement="bottom" />
          </div>
        </div>

        {note.comments.length > 0 && (
          <div className="u-flex u-flex--column u-flex--gap-1">
            {note.comments.map(comment => (
              <NoteComment key={comment.id} comment={comment} onEdit={canWrite ? handleUpdateComment : undefined} />
            ))}
          </div>
        )}

        {canWrite && <CommentForm onSubmit={handleCreateComment} />}
      </Accordion>
      <UserProfileDialog isOpen={!!userDialogId} cwUserId={userDialogId} onClose={() => setUserDialogId('')} />
    </>
  );
}
